import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import React, { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { MenuItem } from "@mui/material"

import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useCurrentSupplier } from "../../../../../suppliers/supplierHooks"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import { deleteProductNumber, fetchProductNumbers, ProductNumbersQuery } from "../productNumbersApi"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { DeleteConfirmDialog } from "../../../../../../shared/components/dialogs/DeleteConfirmDialog"
import {
    getEditSupplierProductGroupNumbersRoute,
    SUPPLIER_ADMIN_CREATE_PRODUCT_NUMBERS_ROUTE,
    SUPPLIER_ADMIN_ROUTE,
} from "../../../../../../constants/routes"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { ProductNumber } from "../productNumbersTypes"
import { DestructiveMenuItem } from "../../../../../../shared/components/menus/DestructiveMenuItem"

export function ProductNumbersList() {
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()
    const { supplierId } = useCurrentSupplier()

    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [productGroupsNumber, setProductGroupsNumber] = useState<ProductNumber>()

    const productNumbers = useApiQuery(fetchProductNumbers, {
        queryName: ProductNumbersQuery.LIST,
        dependencies: {
            supplierId,
        },
        select: (data) => data.productGroupNumbers,
    })

    const { mutate: callDeleteProductGroupNumber } = useApiMutation(deleteProductNumber, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [ProductNumbersQuery.LIST] })
        },
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(productNumbers)

    const columns = useMemo<MRT_ColumnDef<ProductNumber>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("supplier-admin-erp-product-group-numbers-table-header-erp-number"),
                accessorKey: "code",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("supplier-admin-erp-product-group-numbers-table-header-name"),
                accessorKey: "name",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("supplier-admin-erp-product-group-numbers-table-header-status"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<ProductNumber>; closeMenu: () => void }) => [
            <MenuItem
                key={`edit-${row.original.id}`}
                onClick={() => {
                    navigate(getEditSupplierProductGroupNumbersRoute(row.original.id))
                    closeMenu()
                }}
            >
                <LocalizedStrict id="supplier-admin-erp-product-group-numbers-table-button-edit">
                    <span>Edit</span>
                </LocalizedStrict>
            </MenuItem>,
            <DestructiveMenuItem
                key={`delete-${row.original.id}`}
                onClick={() => {
                    setProductGroupsNumber(row.original)
                    setShowConfirmModal(true)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="supplier-admin-erp-product-group-numbers-table-button-delete">
                    <span>Delete</span>
                </LocalizedStrict>
            </DestructiveMenuItem>,
        ],
        [],
    )

    return (
        <>
            <LocalizedStrict id="supplier-admin-erp-product-group-numbers-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPLIER_PRODUCTS_GROUPS_NUMBERS_LIST}
                    title="Product Numbers"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="supplier-admin-erp-product-group-numbers-create-new-button">
                            <StandardButton onClick={() => navigate(SUPPLIER_ADMIN_CREATE_PRODUCT_NUMBERS_ROUTE)}>
                                Create new
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-erp">
                            <Breadcrumb>ERP</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-product-numbers">
                            <Breadcrumb>Product numbers</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={renderRowActionMenuItems}
            />

            {/*Confirm delete*/}
            <LocalizedStrict
                id="supplier-admin-erp-product-group-numbers-delete-entity-dialog"
                attrs={{ typeOfEntity: true }}
            >
                <DeleteConfirmDialog
                    typeOfEntity="Product numbers"
                    entityName={productGroupsNumber?.name || ""}
                    handleClick={() => {
                        if (!productGroupsNumber?.id) throw new Error("Product Groups Number id is undefined")

                        callDeleteProductGroupNumber({ id: productGroupsNumber.id })
                    }}
                    onClose={() => setShowConfirmModal(false)}
                    open={showConfirmModal}
                />
            </LocalizedStrict>
        </>
    )
}
